import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { Moment } from 'moment';

import { get } from '../api/requester';
import type { CleaningUniverse, Universe, UniverseReference } from '../types/commons';
import { getCurrentCountry } from '../utils/country';
import { FORMATS, formatDateDeprecated } from '../utils/date';
import moment from '../utils/moment';

const formatDateForAPI = (date: Moment) => formatDateDeprecated(date, FORMATS.DATETIME.TIMESTAMPS);

type UseGetUniverseAtDateParams = Pick<UseQueryOptions<Universe>, 'enabled' | 'keepPreviousData'> & {
  reference: UniverseReference;
  date: Moment;
  universe?: Universe;
};

type UseGetUniverseAtDateType = Universe | CleaningUniverse;
export const useGetUniverseAtDateQuery = <T extends UseGetUniverseAtDateType = Universe>({
  date,
  reference,
  enabled,
  keepPreviousData,
}: UseGetUniverseAtDateParams) => {
  const {
    data: universe,
    isLoading: isLoadingUniverse,
    error: getUniverseError,
  } = useQuery<T, Error>({
    queryKey: ['universeAtDate', reference, formatDateForAPI(date), getCurrentCountry()],
    queryFn: () => get(`/universes/${reference}/at/${formatDateForAPI(date)}`),
    // Universe at a certain date in time will always be the same by definition
    // That is why we put Infinity as staleTime and cacheTime
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled,
    keepPreviousData,
  });

  return { universe, isLoadingUniverse, getUniverseError };
};

// We put the current country in the key because the result from the back is not the same based on the country so we
// need to create a new cache entry for each query
export const useGetUniversesQuery = (
  options?: Omit<UseQueryOptions<Universe[], Error>, 'queryKey' | 'queryFn' | 'cacheTime' | 'staleTime'>,
) => {
  const {
    data: universes,
    isLoading: isLoadingUniverses,
    error: universesError,
    refetch: refetchUniverses,
  } = useQuery<Universe[], Error>({
    queryKey: ['universes', getCurrentCountry()],
    queryFn: () => get('/universes'),
    ...options,
    // refetchInterval refetches data only when the app is in foreground.
    // By setting the cacheTime to Infinity, we avoid empty cache
    // when the app is back in foreground after more than a day (and when data is being refetched).
    cacheTime: Infinity,
    refetchInterval: moment.duration(1, 'day').asMilliseconds(),
  });
  return { universes, isLoadingUniverses, universesError, refetchUniverses };
};
